<script setup>
/**
 * The top bar's notice that the analysis - the snapshot dashboards, reports and Cube read -
 * is behind the working pages, with Build now for analyst and up
 * (docs/plans/live-working-layer.md §2.17). In the top bar only, never inside a dashboard,
 * so shared and exported dashboards stay clean. Silent while the analysis is current.
 *
 * Read again on every page change and every five minutes.
 */
import { computed, onBeforeUnmount, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useToast } from 'primevue/usetoast'

import NavyButton from '@/volt/NavyButton.vue'
import { useAnalysisStore } from '@/stores/analysis'
import { analysisNotice } from '@/utils/analysis'

const REREAD_MS = 5 * 60 * 1000

const analysis = useAnalysisStore()
const route = useRoute()
const toast = useToast()

const notice = computed(() => analysisNotice(analysis.status))

let timer = null

onMounted(() => {
    analysis.read()
    timer = setInterval(() => analysis.read(), REREAD_MS)
})
onBeforeUnmount(() => clearInterval(timer))

watch(() => route?.fullPath, () => analysis.read())

async function buildNow() {
    try {
        await analysis.build()
        toast.add({ severity: 'success', summary: 'Analysis built', detail: 'Dashboards and reports now show the latest answers.', life: 4000 })
    } catch {
        toast.add({ severity: 'error', summary: 'Analysis not built', detail: analysis.error, life: 6000 })
    }
}
</script>

<template>
    <div v-if="notice" class="flex items-center gap-2 text-xs text-surface-600 dark:text-surface-300" role="status">
        <i class="pi pi-history text-xs" aria-hidden="true" />
        <span>{{ notice }}</span>
        <NavyButton
            v-if="analysis.canBuild"
            label="Build now"
            size="small"
            text
            class="text-xs underline"
            :loading="analysis.building"
            :disabled="analysis.building"
            @click="buildNow"
        />
    </div>
</template>
